import React, { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

//Common
import { LoadingIndicator } from "../../common/VFX-UI";

//App
import States from "./States";
import { queryKeys, regionLookupId } from "./constants";
import stateApi from "../../../app/model/api/stateApi";
import { StateContextData, StatesUseStateType } from "../../../types/state";
import lookupValueApi from "../../../app/model/api/lookupValueApi";
import {
  selectIsActive,
  setIsActive,
  selectSearchValue,
  setSearchValue,
} from "../../../store/state";
import { SearchValue } from "../../../store/types";
import { EMPTY_GUID, GRID_PAGE_SIZE } from "../../../utility/constants/constants";
import { SortBy } from "../../../types";

export const StatesContext = React.createContext<StateContextData | null>(null);

const StatesContainer = () => {
  const dispatch = useDispatch();
  const [state, setState] = useState<StatesUseStateType>({
    stateId: null,
    activeToggle: useSelector(selectIsActive) ?? true,
    searchPayload: useSelector(selectSearchValue) ?? null,
    pageSize: GRID_PAGE_SIZE,
    currentPage: 1,
    sortBy: [{ field: "name", direction: 1 }],
  });

  const { isLoading: isLookupsDataLoading, data: stateLookupData } = useQuery({
    queryKey: [queryKeys.getLookupValues],
    queryFn: () =>
      lookupValueApi.getAllByLookupTypeId({
        lookupTypeId: regionLookupId,
        isActive: true,
      }),
  });

  const {
    isLoading,
    data: statesData,
    refetch: refetchStates,
  } = useQuery({
    queryKey: [queryKeys.getStates],
    queryFn: () =>
      stateApi.searchStates({
        ...state.searchPayload,
        isActive: state.activeToggle,
        pageSize: state.pageSize,
        pageNumber: state.currentPage,
        sortBy: state.sortBy,
      }),
  });

  const { mutate: deleteMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await stateApi.delete(value);
    },
    onError: () => console.log("error"),
    onSuccess: () => console.log("success"),
  });

  const { mutate: activateMutation } = useMutation<void, Error, string>({
    mutationFn: async (value: string) => {
      await stateApi.activate(value);
    },
    onError: () => console.log("error"),
    onSuccess: () => console.log("success"),
  });

  const updateActiveToggle = async () => {
    dispatch(setIsActive());
    setState((prevState) => ({
      ...prevState,
      activeToggle: !prevState.activeToggle,
    }));
  };

  const stateQuery = useQuery({
    queryKey: [queryKeys.getState, state.stateId],
    queryFn: () => stateApi.get(state.stateId ?? EMPTY_GUID),
    enabled: state.stateId !== null,
  });

  const updateSearchPayload = async (values: SearchValue | null) => {
    if (values?.name) {
      dispatch(setSearchValue({ name: values.name }));
    }
    if (values && Object.keys(values).length) {
      setState((prevState) => ({
        ...prevState,
        searchPayload: values,
        currentPage: 1,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        searchPayload: null,
        currentPage: 1,
        sortBy: [{ field: "name", direction: 1 }],
      }));
    }
  };

  const updateSortBy = async (sortBy: SortBy) => {
    const { field, direction } = sortBy;
    setState((prevState) => ({
      ...prevState,
      sortBy: [{ field, direction }],
    }));
  };

  const updateCurrentPage = async (currentPage: number) => {
    setState((prevState) => ({
      ...prevState,
      currentPage: currentPage,
    }));
  };

  const updateCurrentPageSize = async (pageSize: number) => {
    setState((prevState) => ({
      ...prevState,
      pageSize: pageSize,
    }));
  };

  const updateStateId = async (stateId: string | null) => {
    setState((prevState) => ({
      ...prevState,
      stateId: stateId,
    }));
  };

  const contextValue: StateContextData = {
    lookupsData: stateLookupData?.result,
    data: stateQuery?.data?.result,
    id: state.stateId,
    activeToggle: state.activeToggle,
    setId: updateStateId,
    refetchGrid: refetchStates,
    setActiveToggleValue: updateActiveToggle,
    setSearchValue: updateSearchPayload,
    setCurrentPage: updateCurrentPage,
    setCurrentPageSize: updateCurrentPageSize,
    setSortBy: updateSortBy,
    paging: {
      currentPage: state.currentPage,
      totalSize: statesData?.totalSize ?? 0,
    },
    pageSize: state.pageSize,
  };

  return isLoading || isLookupsDataLoading ? (
    <LoadingIndicator />
  ) : (
    <StatesContext.Provider value={contextValue}>
      <States
        data={statesData?.result}
        deleteMutation={deleteMutation}
        activateMutation={activateMutation}
      />
    </StatesContext.Provider>
  );
};

export default StatesContainer;
